import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Crown, Check, Loader2, Zap, CreditCard, Settings } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', CNY: '¥', SEK: 'kr', NOK: 'kr', DKK: 'kr',
};

export default function SubscriptionPlans({ onSuccess }) {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [managingSubscription, setManagingSubscription] = useState(false);

  useEffect(() => { loadPlans(); }, [selectedCurrency]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadPlans = async () => {
    try {
      const data = await api.getPlans(selectedCurrency);
      setPlans(data.plans || []);
      setCurrencies(data.currencies || ['EUR']);
    } catch (err) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const status = await api.getCheckoutStatus(sessionId);
      if (status.payment_status === 'paid') {
        await refreshUser();
        toast.success('Subscription activated!');
        if (onSuccess) onSuccess();
      }
    } catch (err) { console.error('Payment check failed:', err); }
  };

  const handleSubscribe = async (planId) => {
    setCheckingOut(planId);
    try {
      const { checkout_url } = await api.createCheckout(planId, selectedCurrency);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const { portal_url } = await api.createCustomerPortal();
      window.location.href = portal_url;
    } catch (err) {
      toast.error(err.message || 'Failed to open subscription management');
      setManagingSubscription(false);
    }
  };

  const isCurrentPlan = (planId) => user?.subscription_status === 'active' && user?.subscription_plan === planId;
  const isActiveSubscriber = user?.subscription_status === 'active';

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-3">
      {/* Status */}
      <div className={`p-2 rounded-lg text-sm ${
        isActiveSubscriber ? 'bg-green-900/30 border border-green-700' : 
        user?.subscription_status === 'trial' ? 'bg-cyan-900/30 border border-cyan-700' : 'bg-slate-800'
      }`}>
        <p className="text-white font-semibold flex items-center gap-2">
          {isActiveSubscriber ? <><Crown className="h-4 w-4 text-amber-500" /> Pro</> : 
           user?.subscription_status === 'trial' ? <><Zap className="h-4 w-4 text-cyan-400" /> Trial</> : 'No Sub'}
          {user?.subscription_end && <span className="text-slate-400 text-xs ml-auto">
            {new Date(user.subscription_end).toLocaleDateString()}
          </span>}
        </p>
        {isActiveSubscriber && (
          <Button size="sm" variant="outline" className="w-full mt-2 border-green-600 text-green-400" 
            onClick={handleManageSubscription} disabled={managingSubscription}>
            {managingSubscription ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Settings className="h-4 w-4 mr-1" /> Manage</>}
          </Button>
        )}
      </div>

      {/* Currency */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">Currency:</span>
        <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
          <SelectTrigger className="w-16 h-7 text-xs bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* PLANS WITH BUTTONS */}
      {plans.map((plan) => (
        <div key={plan.id} className={`p-3 rounded-lg border ${
          plan.id === 'yearly' ? 'bg-amber-900/20 border-amber-600' : 'bg-slate-800 border-slate-700'
        }`}>
          {/* Header + Price */}
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="text-white font-bold">{plan.name}</span>
              {plan.id === 'yearly' && <Badge className="ml-2 bg-amber-500 text-black text-xs">SAVE 17%</Badge>}
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-white">{CURRENCY_SYMBOLS[selectedCurrency]}{plan.price}</span>
              <span className="text-slate-400 text-sm">/{plan.interval}</span>
            </div>
          </div>

          {/* Trial badge */}
          {plan.trial_days > 0 && !isActiveSubscriber && (
            <Badge className="bg-cyan-900/50 text-cyan-300 text-xs mb-2">{plan.trial_days} days free</Badge>
          )}

          {/* Features inline */}
          <p className="text-xs text-slate-400 mb-3">✓ Cloud sync ✓ Unlimited history ✓ Priority support</p>

          {/* ========== SUBSCRIBE BUTTON ========== */}
          <Button
            className={`w-full h-11 text-base font-bold ${
              plan.id === 'yearly'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black'
                : 'bg-orange-600 hover:bg-orange-500 text-white'
            }`}
            onClick={() => handleSubscribe(plan.id)}
            disabled={checkingOut === plan.id || isCurrentPlan(plan.id)}
            data-testid={`subscribe-${plan.id}-btn`}
          >
            {checkingOut === plan.id ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...</>
            ) : isCurrentPlan(plan.id) ? (
              <><Check className="mr-2 h-5 w-5" /> Current Plan</>
            ) : (
              <><CreditCard className="mr-2 h-5 w-5" /> SUBSCRIBE NOW</>
            )}
          </Button>
        </div>
      ))}

      <p className="text-center text-xs text-slate-500">🎁 14 days free • Cancel anytime</p>
    </div>
  );
}
